// ChatMessageList — Presentational scrollable message list for the AI chat
// modal: the mapped message bubbles, the "Dan is thinking…" typing indicator,
// and the scroll-anchor element.
//
// Extracted verbatim from `AIChatBot.js` (the "Messages" container block) as
// part of the behavior-preserving refactor (issue #240). Render-only, wrapped
// in React.memo. Markup, styles, the `messages.map` → `ChatMessageBubble`
// keying, the `isLoading` typing-indicator guard, and the trailing
// scroll-anchor `<div ref={messagesEndRef} />` are identical to the source.
//
// The `messagesEndRef` is owned by the container / scroll hook and passed in as
// a prop; this component performs no scrolling, fetching, or state — it only
// renders. When `messages` is empty it renders just the (optional) typing
// indicator and the anchor, matching the source's fallback render.
//
// Props:
//   messages       - array of message objects to render
//   isLoading      - boolean, renders the typing indicator when true
//   onQuickReply   - callback passed through to each ChatMessageBubble
//   tripData       - the trip object, passed through to each ChatMessageBubble
//   formatTime     - (Date) => string formatter, passed through to each bubble
//   messagesEndRef - ref attached to the trailing scroll-anchor element
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Bot } from 'lucide-react';
import ChatMessageBubble from './ChatMessageBubble';

export const ChatMessageList = React.memo(function ChatMessageList({
  messages,
  isLoading,
  onQuickReply,
  tripData,
  formatTime,
  messagesEndRef,
}) {
  return (
    <div style={{
      flex: 1,
      padding: '16px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    }}>
      {messages.map((message) => (
        <ChatMessageBubble
          key={message.id}
          message={message}
          isLoading={isLoading}
          onQuickReply={onQuickReply}
          tripData={tripData}
          formatTime={formatTime}
        />
      ))}

      {/* Typing indicator */}
      {isLoading && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <div style={{
            width: '32px', height: '32px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0
          }}>
            <Bot size={16} color="white" />
          </div>
          <div style={{
            background: '#f3f4f6',
            padding: '10px 14px',
            borderRadius: '16px',
            fontSize: '14px',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span>Dan is thinking</span>
            <span style={{ letterSpacing: '2px' }}>...</span>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
});

export default ChatMessageList;
